import _ from 'lodash';
import { useMutation, useQuery } from '@apollo/client';

// Queries
import {
  GET_CURR_USER,
  GET_IDLE_SIGN_OUT_MINUTES,
  REQUEST_PASSWORD_RESET,
  RESET_PASSWORD,
  RESET_PASSWORD_WITH_TOKEN, UPDATE_USER_SETTINGS,
} from '../queries/user.queries';
import { useOperationErrorHandler } from '../hooks/use-operation-error-handler';
import { useLogQueryError } from '../hooks/use-log-query-error';
import { pickSettledSettings, settingsBeforeWrite } from '../helpers/user-settings.helpers';
import { setAuthUserSettings, getAuthUser, staffUser } from './auth.service';
import { getCurrMember } from './member.service';
import { messages } from './user.service.messages';
import { DEFAULT_ASSESSMENT_MEMBER_ROLES } from '../../components/Member/MemberList/member-list.consts';
import { EXCLUSIVE_ROLES, MemberListType, ROLE_SUBSTITUTES } from '../../../_configuration';

export const getCurrUser = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, ...rest } = useQuery(GET_CURR_USER, {
    fetchPolicy: 'cache-and-network',
  });

  return { ...rest, user: _.get(data, 'loggedInUser') };
};

// HOOK: the signed-in staff user's idle sign-out window in minutes, or null when it doesn't apply.
export const useIdleSignOutMinutes = ({ skip }: { skip: boolean }) => {
  const { data, error } = useQuery<{ loggedInUser: Pick<IUser, '_id' | 'idleSignOutMinutes'> | null }>(GET_IDLE_SIGN_OUT_MINUTES, {
    fetchPolicy: 'cache-and-network',
    skip,
  });
  useLogQueryError(error, 'getIdleSignOutMinutes');

  return { userId: data?.loggedInUser?._id, minutes: data?.loggedInUser?.idleSignOutMinutes ?? null };
};

// Global "Max admin" gate — `isAdmin` is a User-level flag, not a company role.
// Hook (wraps getCurrUser); honor `loading` to avoid a flash-redirect on guarded routes.
export const hasAdminAccess = () => {
  const { user, loading } = getCurrUser();

  return { isAdmin: Boolean(user?.isAdmin), loading };
};

type RoleCheck = {roles: string[]; anyAgeGroup?: boolean; ageGroupId?: string; assessmentId?: string; debug?: boolean;};

// Determines if member has at least one matching role for specified role group
export const hasAnyRoles = ({debug, ...rest}: RoleCheck) => {
  if (debug) console.log("hasAnyRoles",rest)
  const { member } = getCurrMember();

  return memberHasAnyRoles({...rest, member, debug})
}

// Pure: hasAnyRoles for a member already read, admins included — for checks repeated over many rows.
export const memberHasAnyRoles = ({member, ...rest}: RoleCheck & { member?: IMember }) => {
  if (!member) return false;
  if (member.isAdmin) return true;

  return providedMemberHasAnyRoles({...rest, member})
}

function getSubstituteRoles({roles, memberListType}: { roles: string[]; memberListType: MemberListType; }) {
  return roles.map((r) => (r in ROLE_SUBSTITUTES[memberListType] ? ROLE_SUBSTITUTES[memberListType][r] : null)).filter((v) => v);
}

function findValidSubstituteRole({
  roles, memberListType, ageGroupRole, assessmentRole, superUserRole
}: {
  roles: string[], memberListType: MemberListType, ageGroupRole?: IMemberRoles, assessmentRole?: IMemberRoles, superUserRole?: IMemberRoles,
}) {
  const substituteRoles = getSubstituteRoles({ roles, memberListType })

  for (let i = 0; i < substituteRoles.length; i++) {
    // @ts-ignore
    const substituteOptions = Object.entries(substituteRoles[i])

    for (let j = 0; j < substituteOptions.length; j++) {
      // @ts-ignore
      const [roleGroup, subbableRoles] = substituteOptions[j];
      let sourceRole = null;
      // @ts-ignore
      if (roleGroup === MemberListType.SUPER && superUserRole) {
        sourceRole = superUserRole;
      }
      // @ts-ignore
      else if (roleGroup === MemberListType.ASSESSMENT && assessmentRole) {
        sourceRole = assessmentRole;
      } else {
        sourceRole = ageGroupRole;
      }

      // @ts-ignore
      const foundRole = sourceRole?.roles?.find((r) => subbableRoles?.includes(r));
      if (foundRole) return foundRole;
    }
  }

  return null;
}

export const providedMemberHasAnyRoles = ({member, roles, anyAgeGroup, ageGroupId, assessmentId, debug}: RoleCheck & { member?: IMember }) => {
  // Check any age group first, but only for roles that can exist at age group level
  if (anyAgeGroup && member?.rolesByAgeGroup.some((rolesByAgeGroup) => {
    // Only check age groups for roles that are valid at age group level
    const ageGroupValidRoles = roles.filter(role => 
      EXCLUSIVE_ROLES[MemberListType.AGE_GROUP].includes(role) || 
      EXCLUSIVE_ROLES[MemberListType.SUPER].includes(role) ||
      (role in ROLE_SUBSTITUTES[MemberListType.AGE_GROUP])
    );
    return ageGroupValidRoles.length > 0 && providedMemberHasAnyRoles({roles: ageGroupValidRoles, ageGroupId: rolesByAgeGroup.ageGroupId, member, debug});
  })) return true

  if (debug) console.log({member, roles, anyAgeGroup, ageGroupId, assessmentId})
  if (member) {
    if (member.isAdmin) return true;

    const ageGroupRole = member.rolesByAgeGroup.find(r => r.ageGroupId === ageGroupId)
    const assessmentRole = member.rolesByAssessment.find(r => r.assessmentId === assessmentId) ?? {assessmentId, roles: DEFAULT_ASSESSMENT_MEMBER_ROLES}
    const superUserRole = {roles: member.superUserRoles}
    if (debug) console.log({ageGroupRole, assessmentRole, superUserRole})

    // Allow proceeding past initial check if roles we are looking for contain exlusive roles that are higher up the role group chain of command
    let canProceed = true
    if (ageGroupId && canProceed) {
      canProceed = false
      if (!!ageGroupRole && hasAnyRoleMatch(ageGroupRole.roles, roles)) return true;
      if (!!findValidSubstituteRole({roles, memberListType: MemberListType.AGE_GROUP, ageGroupRole, assessmentRole, superUserRole})) return true
      if ([...EXCLUSIVE_ROLES[MemberListType.ASSESSMENT], ...EXCLUSIVE_ROLES[MemberListType.SUPER]].some(r => roles.includes(r))) canProceed = true
    }

    if (assessmentId && canProceed) {
      canProceed = false
      if (hasAnyRoleMatch(assessmentRole.roles, roles)) return true;
      if (!!findValidSubstituteRole({ roles, memberListType: MemberListType.ASSESSMENT, assessmentRole, superUserRole })) return true;
      if (EXCLUSIVE_ROLES[MemberListType.SUPER].some(r => roles.includes(r))) canProceed = true
    }

    if (canProceed) {
      canProceed = false
      if (!!superUserRole && hasAnyRoleMatch(superUserRole.roles, roles)) return true;
      return !!findValidSubstituteRole({ roles, memberListType: MemberListType.SUPER, superUserRole });
    }
  }

  return false;
}

// Pure scope-spanning role check at SUPER (trickled), ASSESSMENT, or any AGE_GROUP.
// anyAgeGroup falls through to the SUPER check, so no separate no-scope branch is needed.
export const providedMemberHasRoleInAnyScope = (
  {member, assessments, roles}: {member?: IMember; assessments: IAssessment[]; roles: string[]}
): boolean =>
  !!member && (
    providedMemberHasAnyRoles({member, roles, anyAgeGroup: true})
    || assessments.some((a) => providedMemberHasAnyRoles({member, roles, assessmentId: a._id}))
  );

export const getAllowedMemberRoles = (
  {member, roles, ageGroupId, assessmentId}: {roles: string[]; ageGroupId?: string; assessmentId?: string; member?: IMember}
): IAllowedRole[] => {
  const allowedRoles: IAllowedRole[] = [];

  if (member) {
    if (member.isAdmin) return roles.map((role) => ({role, admin: true}));

    const ageGroupRole = member.rolesByAgeGroup.find(r => r.ageGroupId === ageGroupId)
    const assessmentRole = member.rolesByAssessment.find(r => r.assessmentId === assessmentId) ?? {assessmentId, roles: DEFAULT_ASSESSMENT_MEMBER_ROLES}
    const superUserRole = {roles: member.superUserRoles}


    // Allow proceeding past initial check if roles we are looking for contain exlusive roles that are higher up the role group chain of command
    let canProceed = true

    // Check age group roles
    if (ageGroupId && canProceed) {
      canProceed = false

      // Find allowed age group specific roles
      if (ageGroupRole) {
        ageGroupRole.roles.forEach((role) => {
          if (hasAnyRoleMatch([role], roles)) allowedRoles.push({role, ageGroupId})
        })
      }

      // Find substitute allowed age group specific roles
      const remainingRoles = roles.filter(r => !allowedRoles.some(ar => ar.role === r))
      if (remainingRoles.length) {
        remainingRoles.forEach((role) => {
          if (findValidSubstituteRole({ roles, memberListType: MemberListType.AGE_GROUP, ageGroupRole, assessmentRole, superUserRole })) allowedRoles.push({role, assessmentId})
        })
      }

      // If there are higher level roles in the role check list, then continue finding matching allowed roles
      if ([...EXCLUSIVE_ROLES[MemberListType.ASSESSMENT], ...EXCLUSIVE_ROLES[MemberListType.SUPER]].some(r => roles.includes(r))) canProceed = true
    }

    // Check assessment roles
    if (assessmentId && canProceed) {
      canProceed = false

      // Find allowed assessment specific roles
      // if (hasAnyRoleMatch(assessmentRole.roles, roles)) return true;
      if (assessmentRole)
        assessmentRole.roles.forEach((role) => {
          if (hasAnyRoleMatch([role], roles)) allowedRoles.push({role, assessmentId})
        })

      // Find substitute allowed assessment specific roles
      const remainingRoles = roles.filter(r => !allowedRoles.some(ar => !ar.ageGroupId && !ar.assessmentId && ar.role === r))
      if (remainingRoles.length) {
        remainingRoles.forEach((role) => {
          if (findValidSubstituteRole({ roles, memberListType: MemberListType.ASSESSMENT, assessmentRole, superUserRole })) allowedRoles.push({role, assessmentId})
        })
      }

      // If there are higher level roles in the role check list, then continue finding matching allowed roles
      if (EXCLUSIVE_ROLES[MemberListType.SUPER].some(r => roles.includes(r))) canProceed = true
    }

    // Check super user roles
    if (canProceed && superUserRole?.roles?.length) {
      canProceed = false

      // Find allowed association specific roles
      superUserRole.roles.forEach((role) => {
        if (hasAnyRoleMatch([role], roles)) allowedRoles.push({role})
      })

      // Find substitute allowed association specific roles
      const remainingRoles = roles.filter(r => !allowedRoles.some(ar => !ar.ageGroupId && !ar.assessmentId && ar.role === r))
      if (remainingRoles.length) {
        remainingRoles.forEach((role) => {
          if (findValidSubstituteRole({ roles, memberListType: MemberListType.SUPER, superUserRole })) allowedRoles.push({role})
        })
      }
    }
  }

  return allowedRoles;
}

export const ageGroupHasAnyRoleMatch = ({member, ageGroup, requiredRoles = []}: {member: IMember; ageGroup: IAgeGroup; requiredRoles?: string[]} ) => {
  return member.rolesByAgeGroup.find(r => r.ageGroupId === ageGroup._id && providedMemberHasAnyRoles({member, roles: requiredRoles, ageGroupId: ageGroup._id}))
};

const hasAnyRoleMatch = (sourceRoles: string[], toFindRoles: string[]) => {
  return _.difference(toFindRoles, sourceRoles).length !== toFindRoles.length;
}

export const buildUpdateUserSettings = () => {
  const user = getAuthUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(UPDATE_USER_SETTINGS);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleSettingsError = useOperationErrorHandler(messages.errSettingsNotSaved);

  return {
    ...rest,
    // Resolves whether the write saved, never rejects. A correction the app makes on its own (userInitiated: false)
    // keeps its value on failure and only logs, so a component re-deriving it on every render can't loop.
    updateUserSettings: (settings: Record<string, unknown>, { userInitiated = true }: { userInitiated?: boolean } = {}): Promise<boolean> => {
      const before = staffUser();
      setAuthUserSettings(settings);

      // Settles only the fields this write set, so a change made while it was in flight is kept.
      const settle = (settled?: Record<string, unknown>) => {
        const changes = pickSettledSettings({ current: staffUser()?.settings, written: settings, settled });
        if (!_.isEmpty(changes)) setAuthUserSettings(changes);
      };

      return mutation({ variables: { _id: user?._id, settings } }).then(
        ({ data }) => {
          settle(data?.updateUserSettings?.settings);
          return true;
        },
        (error) => {
          if (!userInitiated) {
            console.error('updateUserSettings failed:', error);
            return false;
          }
          settle(settingsBeforeWrite({ before: before?.settings, written: settings }));
          handleSettingsError('updateUserSettings', error);
          return false;
        },
      );
    }
  }
}

export const buildResetPassword = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [resetPassword, rest] = useMutation(RESET_PASSWORD);

  return {
    ...rest,
    resetPassword: async (password: string) =>
      resetPassword({ variables: { password } })
  };
}

export const buildRequestPasswordReset = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [requestPasswordReset, rest] = useMutation(REQUEST_PASSWORD_RESET, {
    errorPolicy: 'all',
  });

  return {
    ...rest,
    requestPasswordReset: async (email: string) =>
      requestPasswordReset({ variables: { email } })
  }
};

export const buildResetPasswordWithToken = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [resetPasswordWithToken, rest] = useMutation(RESET_PASSWORD_WITH_TOKEN);

  return {
    ...rest,
    resetPasswordWithToken: (variables: any) =>
      resetPasswordWithToken({ variables })
  };
};
